import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Slider,
  Button,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import getCroppedImg from 'common/utils/cropImage';

/**
 * Modal "Ajustar foto": reposicionar (drag) + zoom + recorte con react-easy-crop.
 * Al guardar devuelve un File JPEG recortado (onSave(file)). Estilo monocromo OTRIX.
 * Parametrizable: `aspect` y `cropShape` ('round' para avatar, 'rect' para portadas de plan).
 */
function AvatarCropModal({
  open,
  imageSrc = null,
  onCancel,
  onSave,
  aspect = 1,
  cropShape = 'round',
  title = 'Ajustar foto',
  saveLabel = 'Guardar foto',
  minZoom = 1,
  restrictPosition = true
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  // Al cambiar la imagen a recortar (p. ej. otro fotograma), reiniciar encuadre/zoom y volver a
  // "cargando": la instancia queda montada entre aperturas y no debe heredar el recorte anterior.
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setAreaPixels(null);
  }, [imageSrc]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setAreaPixels(croppedAreaPixels);
  }, []);

  // El área de recorte existe solo cuando la imagen cargó y react-easy-crop midió el
  // encuadre. Antes de eso, "Guardar" era un no-op SILENCIOSO: en móvil (foto grande +
  // tap rápido) el usuario guardaba varias veces sin que pasara nada y la portada
  // quedaba sin adjuntar. Ahora el botón espera deshabilitado con loader.
  const isCropReady = Boolean(imageSrc && areaPixels);

  const handleSave = async () => {
    if (!isCropReady) return;
    setSaving(true);
    try {
      const file = await getCroppedImg(imageSrc, areaPixels);
      onSave(file);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main', color: '#fff' } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 0.5 }}>
        {title}
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.55)', fontWeight: 400 }}>
          Arrastra para reposicionar · usa el zoom
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Box sx={{ position: 'relative', width: '100%', height: 300, borderRadius: 2, overflow: 'hidden', backgroundColor: '#000', mt: 1 }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              aspect={aspect}
              cropShape={cropShape}
              restrictPosition={restrictPosition}
              showGrid={cropShape === 'rect'}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <IconButton size="small" onClick={() => setZoom((z) => Math.max(minZoom, +(z - 0.2).toFixed(2)))} sx={{ color: '#fff' }}>
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Slider value={zoom} min={minZoom} max={3} step={0.05} onChange={(_, v) => setZoom(v)} aria-label="Zoom" sx={{ color: '#F9BD4E' }} />
          <IconButton size="small" onClick={() => setZoom((z) => Math.min(3, +(z + 0.2).toFixed(2)))} sx={{ color: '#fff' }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
          <Button
            onClick={onCancel}
            disabled={saving}
            fullWidth
            variant="outlined"
            sx={{ color: '#fff', borderColor: 'hairline.strong', fontWeight: 700, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)' } }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!isCropReady || saving}
            fullWidth
            sx={{ fontWeight: 800, background: '#FFFFFF', color: '#0A0A0A', '&:hover': { background: '#FFFFFF', filter: 'brightness(0.94)' }, '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' } }}
          >
            {saving || !isCropReady ? (
              <CircularProgress size={20} sx={{ color: isCropReady ? '#0A0A0A' : 'rgba(255,255,255,0.5)' }} />
            ) : (
              saveLabel
            )}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

AvatarCropModal.propTypes = {
  open: PropTypes.bool.isRequired,
  imageSrc: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  aspect: PropTypes.number,
  cropShape: PropTypes.oneOf(['round', 'rect']),
  title: PropTypes.string,
  saveLabel: PropTypes.string,
  minZoom: PropTypes.number,
  restrictPosition: PropTypes.bool,
};

export default AvatarCropModal;
